<template>
    <div
        ref="searchbar"
        class="relative z-50 flex w-full items-center md:w-auto"
    >
        <div class="relative flex grow items-center">
            <input
                id="searchInput"
                v-model="input"
                type="text"
                class="grow rounded-none border-b border-black-text px-2 py-1 outline-none md:w-36 lg:w-48"
                :placeholder="$t('search.placeholder')"
                @keyup.enter="handleSearchInput"
                @keyup.escape="areSuggestionsVisible = false"
                @keydown.down.prevent="focusElWithIdx(0)"
                @keydown.tab.prevent="focusElWithIdx(0)"
                @keydown.up.prevent="
                    focusElWithIdx(filteredKeywords.length - 1)
                "
            />
            <i
                v-show="searchStore.fetching"
                class="pi pi-spin pi-spinner absolute right-2"
            />
        </div>
        <div
            class="flex cursor-pointer"
            @click="handleSearchInput"
        >
            <Icon
                name="custom:search"
                size="1.125rem"
            />
        </div>
        <teleport
            v-if="store.keywords.length > 0"
            to="body"
        >
            <div
                v-show="areSuggestionsVisible && filteredKeywords.length > 0"
                ref="suggestions"
                class="fixed z-[999] flex w-full max-w-72 translate-y-2 flex-col divide-y divide-black-text border border-black-text bg-white"
            >
                <div
                    v-for="(kw, idx) in filteredKeywords"
                    :id="'suggestionsItem' + idx"
                    :key="kw.ID"
                    tabindex="0"
                    class="cursor-pointer p-2 hover:font-promedium hover:text-hover-link focus:font-promedium focus:text-hover-link focus:outline-none"
                    @click="handleSuggestionClick(useLocaleValue(kw, 'NAZEV'))"
                    @keyup.enter.prevent="
                        handleSuggestionClick(useLocaleValue(kw, 'NAZEV'))
                    "
                    @keydown.up.prevent="focusElWithIdx(idx - 1)"
                    @keydown.down.prevent="focusElWithIdx(idx + 1)"
                >
                    {{ useLocaleValue(kw, "NAZEV") }}
                </div>
            </div>
        </teleport>
    </div>
</template>

<script setup lang="ts">
const { locale } = useI18n()
const searchStore = useSearchStore()
const store = useMainStore()

const searchbar = ref<HTMLElement | null>(null)
const suggestions = ref<HTMLElement | null>(null)

function findCorrectCoordinates() {
    if (searchbar.value && suggestions.value) {
        const searchbarRect = searchbar.value.getBoundingClientRect()
        suggestions.value.style.top = `${searchbarRect.bottom}px`
        suggestions.value.style.left = `${searchbarRect.left}px`
    }
}

const input = ref("")
const areSuggestionsVisible = ref(false)
const isSearching = ref(false)

async function handleSearchInput() {
    areSuggestionsVisible.value = false
    isSearching.value = true
    await searchStore.executeSearch(input.value, locale.value as LanguageCode)
    isSearching.value = false
}

async function handleSuggestionClick(kw: string) {
    input.value = kw
    areSuggestionsVisible.value = false
    isSearching.value = true
    await searchStore.executeSearch(input.value, locale.value as LanguageCode)
    isSearching.value = false
}

function focusElWithIdx(index: number) {
    if (index < 0 || index >= filteredKeywords.value.length) {
        const inputEl = document.querySelector("#searchInput") as HTMLElement
        if (inputEl) {
            inputEl.focus()
        }
    } else {
        const element = document.querySelector(
            `#suggestionsItem${index}`
        ) as HTMLElement
        if (element) {
            element.focus()
        }
    }
}

watch(input, () => {
    if (input.value.length > 1 && isSearching.value === false) {
        findCorrectCoordinates()
        areSuggestionsVisible.value = true
    } else {
        areSuggestionsVisible.value = false
    }
})

onClickOutside(searchbar, () => {
    areSuggestionsVisible.value = false
})

const filteredKeywords = computed(() => {
    return store.keywords.filter(kw =>
        searchStore.includesWithoutDiacritics(
            useLocaleValue(kw, "NAZEV"),
            input.value
        )
    )
})
</script>

<style scoped></style>
